/**
 * Pearl Framework Configuration
 * 
 * Complete system configuration for Pearl Framework - Trauma-Informed Engineering methodology
 * This file contains the TIE-ASM model, nacre traits mapping, ecosystem platforms, and user journey
 * 
 * Last updated: 2026-01-26
 */

// ============= TIE-ASM Five Pillars =============
export const tieAsmPillars = {
  inheritance: {
    id: 'inheritance',
    name: 'Inheritance',
    nameZh: '传承',
    description: 'Audit generational hidden code (family trauma scripts, implicit beliefs)',
    descriptionZh: '审计世代隐性代码（家族创伤脚本、隐性信念）',
    detail: 'Pearl guides users to trace roots, not to indulge in victim identity, but to rewrite pre-installed firmware and transform it into inheritable resilience assets.',
    detailZh: 'Pearl 引导用户追溯根源，不是为了沉溺受害者身份，而是为了重写预装固件，将其转化为可继承的韧性资产。',
    icon: 'GitBranch',
  },
  compatibility: {
    id: 'compatibility',
    name: 'Compatibility',
    nameZh: '兼容性',
    description: 'Identify hardware/OS mismatch between Eastern body wisdom and Western narrative therapy',
    descriptionZh: '识别硬件/OS 不匹配——西方叙事疗法常忽略东方身体存储创伤',
    detail: 'Pearl bridges somatic and flow practices, ensuring the system runs seamlessly across different healing modalities.',
    detailZh: 'Pearl 通过躯体与心流实践桥接，确保系统无缝运行。',
    icon: 'Puzzle',
  },
  feature: {
    id: 'feature',
    name: 'Feature',
    nameZh: '特征',
    description: 'Trauma responses are not bugs, but exploitable subroutines',
    descriptionZh: '创伤反应不是 bug，而是可利用的子程序',
    detail: 'Procrastination, avoidance, hypervigilance are reframed as "energy protection protocols," "strategic retreat engines," or "threat detection radars" for users to actively deploy.',
    detailZh: '拖延、回避、过度警觉被重构为"能量保护协议""战略撤退引擎"或"威胁探测雷达"，供用户主动部署。',
    icon: 'Cpu',
  },
  zeroFriction: {
    id: 'zero-friction',
    name: 'Zero-Friction',
    nameZh: '零摩擦',
    description: 'Start from joy, not pain point digging',
    descriptionZh: '从喜悦启动，而非痛点挖掘',
    detail: 'By entering through flow states, Pearl replaces pain with minimal resistance, avoiding the depletion cycle of traditional pain-point healing.',
    detailZh: '通过心流状态切入，Pearl 以最小阻力置换痛感，避免传统痛点疗愈的耗竭循环。',
    icon: 'Zap',
  },
  transparency: {
    id: 'transparency',
    name: 'Transparency',
    nameZh: '透明度',
    description: 'Strategic open-source - transform shame into trust',
    descriptionZh: '战略性开源——将羞耻转化为信任',
    detail: 'Through public data contracts, users open-source their own "error logs" to co-build collective blueprints and break isolation.',
    detailZh: '通过公开数据契约，用户开源自己的"错误日志"，共建集体蓝图，打破孤立。',
    icon: 'Eye',
  },
} as const;

// ============= Nacre (Pearl Coating) Trait Mapping =============
export const nacreTraits = {
  hypersensitivity: {
    id: 'hypersensitivity',
    traditionalLabel: 'Hypersensitivity',
    traditionalLabelZh: '高敏感',
    traditionalView: 'Glass heart, easily triggered, emotionally unstable',
    traditionalViewZh: '玻璃心、易触发、情绪不稳定',
    pearlReframe: 'High-precision environmental scanning radar',
    pearlReframeZh: '高精度的环境扫描雷达',
    strategicValue: 'Pattern recognition, threat detection, emotional intelligence',
    strategicValueZh: '模式识别、威胁探测、情绪智力',
    icon: 'Radio',
  },
  hypervigilance: {
    id: 'hypervigilance',
    traditionalLabel: 'Hypervigilance / Suspicion',
    traditionalLabelZh: '多疑',
    traditionalView: 'Trust issues, paranoid, unable to relax',
    traditionalViewZh: '信任障碍、偏执、无法放松',
    pearlReframe: 'Independent risk audit protocol',
    pearlReframeZh: '独立的风险审计协议',
    strategicValue: 'Risk assessment, due diligence, strategic planning',
    strategicValueZh: '风险评估、尽职调查、战略规划',
    icon: 'Shield',
  },
  perfectionism: {
    id: 'perfectionism',
    traditionalLabel: 'Perfectionism',
    traditionalLabelZh: '完美主义',
    traditionalView: 'OCD tendencies, never satisfied, self-critical',
    traditionalViewZh: '强迫倾向、永不满足、自我批评',
    pearlReframe: 'Excellence delivery standard',
    pearlReframeZh: '卓越的交付标准',
    strategicValue: 'Quality control, attention to detail, high standards',
    strategicValueZh: '质量控制、细节关注、高标准',
    icon: 'Target',
  },
  avoidance: {
    id: 'avoidance',
    traditionalLabel: 'Avoidance / Procrastination',
    traditionalLabelZh: '回避/拖延',
    traditionalView: 'Lazy, irresponsible, fear of failure',
    traditionalViewZh: '懒惰、不负责任、害怕失败',
    pearlReframe: 'Energy protection protocol & strategic retreat engine',
    pearlReframeZh: '能量保护协议 & 战略撤退引擎',
    strategicValue: 'Resource conservation, boundary setting, energy management',
    strategicValueZh: '资源保存、边界设定、能量管理',
    icon: 'Battery',
  },
  emotionalWall: {
    id: 'emotional-wall',
    traditionalLabel: 'Emotional Detachment',
    traditionalLabelZh: '情感隔离',
    traditionalView: 'Cold, unavailable, intimacy issues',
    traditionalViewZh: '冷漠、不可及、亲密障碍',
    pearlReframe: 'Fortified boundary protocol',
    pearlReframeZh: '坚固边界协议',
    strategicValue: 'Professional boundaries, objectivity, crisis management',
    strategicValueZh: '专业边界、客观性、危机管理',
    icon: 'Brick',
  },
} as const;

// ============= Pearl Ecosystem Platforms =============
export const pearlEcosystem = {
  dataset: {
    id: 'pearl-dataset',
    name: 'Pearl Dataset',
    nameZh: 'Pearl Dataset',
    tagline: 'Open-source survival archives & longitudinal dataset',
    taglineZh: '开源生存档案与纵向数据集',
    description: 'Open-source archive and data warehouse that validates experiences and offers self-mapping tools.',
    descriptionZh: '开源档案和数据仓库，用于验证创伤经历并提供自我映射工具。',
    url: 'https://pearl-dataset.borninsea.com/',
    icon: 'Database',
    features: [
      { name: '60-Second Self-Assessment', nameZh: '60秒自测' },
      { name: 'Sample Archives', nameZh: '样本档案' },
      { name: 'Clinical Indicator Maps', nameZh: '临床指标地图' },
      { name: 'Advanced Tool Registration', nameZh: '高级工具注册' },
    ],
  },
  coach: {
    id: 'pearl-coach',
    name: 'Pearl Coach',
    nameZh: 'Pearl Coach',
    tagline: 'Practical guidance layer',
    taglineZh: '实战指导层',
    description: 'Guidance platform with articles, camps, and 1:1 consulting to shift from inner friction to creation.',
    descriptionZh: '指导平台，提供文章、营地和一对一咨询，帮助用户从内耗转向创造。',
    url: 'https://pearl-coach.borninsea.com/',
    icon: 'GraduationCap',
    features: [
      { name: '70+ Survival Code Articles', nameZh: '70+生存代码文章' },
      { name: 'Resilience Rebuilding Camps', nameZh: '韧性重建营' },
      { name: 'Private Consulting', nameZh: '私人咨询' },
      { name: 'From Love Famine to Energy Autonomy', nameZh: '从爱饥荒到能量自治' },
    ],
  },
  studio: {
    id: 'pearl-studio',
    name: 'Pearl Studio',
    nameZh: 'Pearl Studio',
    tagline: 'Core talent workshop',
    taglineZh: '核心天赋工坊',
    description: 'Talent lab using the TIE-ASM five-pillar architecture for layered transformation from trauma to talent.',
    descriptionZh: '天赋工坊，使用 TIE-ASM（创伤知情工程-自适应系统模型）五支柱架构，实现创伤到天赋的层叠重构。',
    url: 'https://pearl-studio.borninsea.com/',
    icon: 'Wrench',
    features: [
      { name: 'PEARL ARCHITECT Interface', nameZh: 'PEARL ARCHITECT 界面' },
      { name: 'TIE-ASM Decoding Tools', nameZh: 'TIE-ASM 解码工具' },
      { name: 'Subject Dossier', nameZh: '个体标本档案' },
      { name: 'Architecture Modeling', nameZh: '架构建模' },
    ],
  },
} as const;

// ============= User Journey Stages =============
export const userJourney = {
  sleeper: {
    id: 'sleeper',
    name: 'Sleeper',
    nameZh: '沉睡者',
    stage: 1,
    description: 'Self-assessment phase - beginning to recognize patterns',
    descriptionZh: '自测阶段——开始识别模式',
    characteristics: [
      'Feels stuck but unsure why',
      'Experiences recurring patterns in relationships/work',
      'Has vague sense that "something is off"',
    ],
    characteristicsZh: [
      '感到困住但不知为何',
      '在关系/工作中经历重复模式',
      '模糊感觉"有些不对"',
    ],
    action: 'Take the 60-second self-assessment',
    actionZh: '完成60秒自测',
    icon: 'Moon',
  },
  seeker: {
    id: 'seeker',
    name: 'Seeker',
    nameZh: '求索者',
    stage: 2,
    description: 'Sample analysis & decoding phase',
    descriptionZh: '样本分析与解码阶段',
    characteristics: [
      'Actively exploring trauma patterns',
      'Reading survival code articles',
      'Beginning to reframe "flaws" as features',
    ],
    characteristicsZh: [
      '积极探索创伤模式',
      '阅读生存代码文章',
      '开始将"缺陷"重构为特征',
    ],
    action: 'Engage with Pearl Coach content',
    actionZh: '参与 Pearl Coach 内容',
    icon: 'Search',
  },
  cultivator: {
    id: 'cultivator',
    name: 'Cultivator',
    nameZh: '耕耘者',
    stage: 3,
    description: 'Tool deployment & ecosystem integration',
    descriptionZh: '工具部署与生态整合',
    characteristics: [
      'Actively deploying nacre traits strategically',
      'Contributing to open-source archives',
      'Building internal abundance garden',
    ],
    characteristicsZh: [
      '战略性部署珍珠质特质',
      '贡献开源档案',
      '构建内在丰盛花园',
    ],
    action: 'Join Pearl Studio for advanced architecture',
    actionZh: '加入 Pearl Studio 进行高级架构',
    icon: 'Sprout',
  },
} as const;

// ============= Cold Start Method (Energy Reboot) =============
export const coldStartMethod = {
  name: 'Cold Start Method',
  nameZh: '心力耗竭冷启动法',
  description: 'Core methodology for rebooting from complete energy depletion',
  descriptionZh: '从彻底心力耗竭中冷启动的核心方法论',
  steps: {
    stopTheBleeding: {
      id: 'stop-the-bleeding',
      name: 'Stop the Bleeding',
      nameZh: '能量止损',
      order: 1,
      description: 'Completely accept the "no battery" state, stop self-criticism, disconnect non-essential information input, pause non-urgent decisions.',
      descriptionZh: '彻底接受"没电了"的状态，停止自我批评，断开非必要信息输入，暂停非紧急决策。',
      actions: [
        'Accept the depleted state without judgment',
        'Stop all self-criticism loops',
        'Disconnect from non-essential information sources',
        'Pause all non-urgent decisions',
      ],
      actionsZh: [
        '无判断地接受耗竭状态',
        '停止所有自我批评循环',
        '断开非必要信息源',
        '暂停所有非紧急决策',
      ],
      icon: 'Pause',
    },
    trickleCharging: {
      id: 'trickle-charging',
      name: 'Trickle Charging',
      nameZh: '涓流充电',
      order: 2,
      description: 'Through extremely minimal, instinct-aligned activities (like sunbathing, listening to music, completing one task that cannot fail) obtain stable positive feedback.',
      descriptionZh: '通过极微小、符合本能的活动（如晒太阳、听音乐、完成一件绝不会失败的任务）获得稳定的正反馈。',
      actions: [
        'Identify micro-activities that feel good',
        'Complete one task that cannot fail',
        'Sunbathe, listen to music, or other sensory comfort',
        'Collect small wins for stable positive feedback',
      ],
      actionsZh: [
        '识别感觉良好的微活动',
        '完成一件绝不会失败的任务',
        '晒太阳、听音乐或其他感官舒适',
        '收集小成功以获得稳定正反馈',
      ],
      icon: 'BatteryCharging',
    },
    reinvestingDividends: {
      id: 'reinvesting-dividends',
      name: 'Reinvesting Dividends',
      nameZh: '红利再投',
      order: 3,
      description: 'Immediately and consciously invest the weak energy obtained into the next micro-activity that can produce higher returns, forming a positive snowball effect.',
      descriptionZh: '将获得的微弱能量，立刻有意识地投入到能产生更高回报的下一个微小活动中，形成正向滚雪球效应。',
      actions: [
        'Identify next-level micro-activity with slightly higher return',
        'Consciously invest recovered energy',
        'Build momentum through progressive wins',
        'Create positive snowball effect',
      ],
      actionsZh: [
        '识别下一级回报稍高的微活动',
        '有意识地投入恢复的能量',
        '通过渐进胜利建立动力',
        '创造正向滚雪球效应',
      ],
      icon: 'TrendingUp',
    },
  },
} as const;

// ============= Core Identity Tags =============
export const coreIdentityTags = {
  adversitySurvivalExpert: {
    id: 'adversity-survival-expert',
    name: 'Adversity Survival Expert',
    nameZh: '逆境生存专家',
    description: 'Living case of successful survival and development in extreme scarcity (material, emotional, cognitive) environments',
    descriptionZh: '在极端匮乏（物质、情感、认知）环境中成功生存并发展的活体案例',
  },
  lifeArchitect: {
    id: 'life-architect',
    name: 'Life Architect',
    nameZh: '生命架构师',
    description: 'Practitioner who systematically transforms life\'s "grit" (trauma, setbacks) into "pearls" (wisdom, strength)',
    descriptionZh: '将人生的"沙砾"（创伤、挫折）系统性地转化为"珍珠"（智慧、力量）的实践者',
  },
  cognitionDrivenPioneer: {
    id: 'cognition-driven-pioneer',
    name: 'Cognition-Driven Pioneer',
    nameZh: '认知驱动型先驱',
    description: 'Skilled in using rationality, research, and model thinking to solve life dilemmas',
    descriptionZh: '擅长运用理性、研究和模型思维来解决生命困境',
  },
  idealistPractitioner: {
    id: 'idealist-practitioner',
    name: 'Idealist Practitioner',
    nameZh: '理想主义实践者',
    description: 'Long-term commitment to beliefs (medical informatization), valuing intrinsic value over short-term material returns',
    descriptionZh: '为信念（医疗信息化）长期投入，重视内在价值高于短期物质回报',
  },
} as const;

// ============= Target Audience =============
export const targetAudience = {
  seeker: {
    id: 'the-seeker',
    name: 'The Seeker',
    nameZh: '探索者',
    description: 'Highly intelligent, self-aware individuals (28-45 years old) who appear capable and responsible on the outside, but are plagued by persistent "stuckness" and inner friction on the inside.',
    descriptionZh: '高度智能、自我觉察的个体（28-45岁），外表看起来负责能干，但内心深处被一种持续的"卡顿感"和内在摩擦所困扰。',
    characteristics: [
      'Survivors of Childhood Emotional Neglect (CEN)',
      'Unable to internalize their achievements',
      'Feel like "imposters" despite success',
      'High-functioning but internally depleted',
    ],
    characteristicsZh: [
      '儿童情感忽视（CEN）的幸存者',
      '无法将自己的成就内化',
      '尽管成功却感觉像"冒牌货"',
      '高功能但内在耗竭',
    ],
  },
} as const;

// ============= Sovereignty Gap Philosophy =============
export const sovereigntyGap = {
  name: 'Sovereignty Gap',
  nameZh: '主权缺口',
  description: 'The fundamental divergence between Pearl Framework and mainstream approaches',
  descriptionZh: 'Pearl Framework 与主流方法的根本分歧',
  traditionalApproach: {
    description: 'Industrial plunder logic: Set standards → Create defects → Sell acceptance',
    descriptionZh: '工业化掠夺逻辑：设定标准 → 制造残次品 → 贩卖接纳',
    steps: [
      'Establish "healthy" normal distribution based on statistical averages',
      'Define extreme traits (high sensitivity, ambition, detachment) as "deviations" or "symptoms"',
      'Sell "self-acceptance" prescription, making you spend a lifetime trimming yourself to fit mediocre molds',
    ],
    stepsZh: [
      '建立基于统计学平均值的"健康"正态分布',
      '将落在两端的极端特质（高敏感、野心、疏离）定义为"偏差"或"病症"',
      '兜售"自我接纳"的药方，让你花费一生去修剪自己以适应平庸的模具',
    ],
  },
  pearlChallenge: {
    question: 'Who defined the "you" that needs to be accepted?',
    questionZh: '谁定义了那个需要被接纳的你？',
    answer: 'If "negative" itself is a virus implanted by external systems, then "acceptance" is assisting the virus in completing the occupation of spiritual sovereignty.',
    answerZh: '如果"负面"本身就是外部系统植入的病毒，那么"接纳"就是在协助病毒完成对精神主权的占领。',
    solution: 'You don\'t need to accept an emasculated version of yourself. You need to reclaim the judicial power of definition.',
    solutionZh: '你不需要接纳一个被阉割的自我版本，你需要夺回定义的司法权。',
  },
  coreMessage: {
    en: 'The real danger is not refusing acceptance, but accepting a castrated, externally-defined version of yourself. Reclaim your definition rights, layer your pearl. You are not a semi-finished product that needs to be repaired. You are a Pearl Architect under construction.',
    zh: '真正的危险不是拒绝接纳，而是接纳了一个被阉割的、外部定义的自我版本。夺回你的定义权，层叠出属于你的珍珠。你不是需要被修复的半成品，你是正在构建中的 Pearl Architect。',
  },
} as const;

// ============= Origin Background =============
export const originBackground = {
  region: {
    en: 'Gansu Province, Northwestern China (rural)',
    zh: '甘肃农村',
  },
  familyStructure: {
    en: 'Blended family',
    zh: '重组家庭',
  },
  familyDetails: {
    birthMother: {
      en: 'Has intellectual disabilities, left when founder was 1 year old',
      zh: '有智力障碍，在创始人一岁时离开',
    },
    birthFather: {
      en: 'Disabled, lost his father in infancy',
      zh: '残疾，襁褓丧父',
    },
    stepMother: {
      en: 'Good person, lost her mother in childhood, raised by her father',
      zh: '人很好，幼年丧母，由父亲带大',
    },
    literacy: {
      en: 'All three parents are illiterate (cannot write their own names)',
      zh: '三位父母均不识字（无法书写自己名字）',
    },
  },
  upbringing: {
    en: 'Raised by grandmother, but with weak emotional connection ("Grandma never really comforted me")',
    zh: '由奶奶带大，但情感联结薄弱（"奶奶真真没有安慰过我"）',
  },
  economicStatus: {
    en: 'Impoverished ("family has three acres of land")',
    zh: '家境贫困（"家有田三亩"）',
  },
} as const;

// ============= Early Development =============
export const earlyDevelopment = {
  developmentalDelays: {
    en: 'Couldn\'t speak at 3, couldn\'t walk at 4',
    zh: '三岁不会说话，四岁不会走路',
  },
  healthChallenges: [
    {
      issue: 'Incontinence',
      issueZh: '大小便失禁',
      period: 'Elementary school',
      periodZh: '小学阶段',
    },
    {
      issue: 'Bedwetting until age 11-12',
      issueZh: '尿床至11-12岁',
      period: 'Until pre-teen',
      periodZh: '直到少年期',
    },
    {
      issue: 'Hand tremors, diagnosed with non-specific hyperthyroidism',
      issueZh: '手抖，被诊断为非特异性甲亢',
      period: 'Ongoing',
      periodZh: '持续',
    },
  ],
  coreTrauma: {
    name: 'Childhood Emotional Neglect (CEN)',
    nameZh: '儿童情感忽视（CEN）',
    awarenessAge: 38,
    coreSymptoms: {
      en: ['Unable to ask for help', 'Never makes requests', 'Suppresses own needs'],
      zh: ['无法求助', '从不提要求', '压抑自身需求'],
    },
    innerFeeling: {
      en: 'Deep sense of "being worthless" and "being abandoned"',
      zh: '内在有深刻的"废物感"和"被遗弃感"',
    },
    externalVsInternal: {
      external: {
        en: 'Optimistic and cheerful on the surface ("always happy")',
        zh: '表面上乐观开朗（"天天乐呵呵"）',
      },
      internal: {
        en: 'Internal exhaustion from long-term suppression, severe energy drain',
        zh: '内心因长期压抑而持续"内耗"，能量被严重消耗',
      },
    },
  },
} as const;

// ============= Narrative Core =============
export const narrativeCore = {
  model: 'Hero\'s Journey',
  modelZh: '英雄之旅',
  description: 'A story of someone who, from a starting point where "all indicators predicted failure," through self-research and extreme inner drive, step by step solved life\'s puzzles and ultimately achieved cognitive and spiritual transcendence.',
  descriptionZh: '一个从"所有指标均预示失败"的起点，通过自我研究和极致的内驱力，一步步解开困境谜题，并最终实现认知和精神层面逆袭的故事。',
  catalyst: {
    en: 'The true catalyst for the entire "system reinstallation" was a complete inner energy depletion (heart exhaustion). This "crash" broke the old, unsustainable survival patterns and created necessary conditions for building a new system.',
    zh: '整个"系统重装"的真正催化剂，是一次彻底的内心能量耗尽（心力耗竭）。这次"宕机"打破了旧有的、不可持续的生存模式，为新系统的建立创造了必要条件。',
  },
  metaphors: {
    pearlMethod: {
      name: 'The Pearl Method',
      nameZh: '珍珠之法',
      description: 'The mental technique of transforming life\'s trauma and setbacks (grit) into inner strength and wisdom (pearl) through conscious wrapping and tempering.',
      descriptionZh: '将人生的创伤与挫折（沙砾），通过有意识的包裹与淬炼，最终孕育成内在力量与智慧（珍珠）的心法。',
    },
    pressureTest: {
      name: 'Pressure Test',
      nameZh: '压力测试',
      description: 'Redefining past suffering as the ultimate test of one\'s own system strength.',
      descriptionZh: '将过去的苦难重新定义为对自身系统强度的终极测试。',
    },
    researchSample: {
      name: 'Research Sample',
      nameZh: '研究样本',
      description: 'Viewing oneself as an extremely valuable human adversity survival research case.',
      descriptionZh: '将自己视为一个极具价值的人类逆境生存研究案例。',
    },
  },
} as const;

// ============= Value Proposition =============
export const valueProposition = {
  corePromise: {
    en: 'Provide a "transforming grit into pearl" mental technique',
    zh: '提供一套"化沙为珠"的心法',
  },
  coreTeaching: {
    en: 'Teaching how to transform adversity into inner drive and wisdom',
    zh: '教授如何将逆境转化为内在动力和智慧',
  },
  deliverables: {
    en: 'Not emotional comfort, but personally-tested thinking models, energy management protocols, and narrative tempering tools',
    zh: '不是情感慰藉，而是经过亲测的思维模型、能量管理协议和叙事淬炼工具',
  },
  tools: [
    {
      name: 'A-R-C Narrative Reconstruction Method',
      nameZh: 'A-R-C叙事重构法',
    },
    {
      name: 'Fasting Thinking',
      nameZh: '辟谷思维',
    },
    {
      name: 'Flow Reading Method',
      nameZh: '心流悦读法',
    },
  ],
} as const;

// ============= Export All =============
export const pearlFramework = {
  tieAsmPillars,
  nacreTraits,
  pearlEcosystem,
  userJourney,
  coldStartMethod,
  coreIdentityTags,
  targetAudience,
  sovereigntyGap,
  originBackground,
  earlyDevelopment,
  narrativeCore,
  valueProposition,
} as const;

export type PearlFramework = typeof pearlFramework;
export type TieAsmPillar = typeof tieAsmPillars[keyof typeof tieAsmPillars];
export type NacreTrait = typeof nacreTraits[keyof typeof nacreTraits];
export type PearlPlatform = typeof pearlEcosystem[keyof typeof pearlEcosystem];
export type UserJourneyStage = typeof userJourney[keyof typeof userJourney];
